import {
  IBankDetailsBase,
  IContactDetailsBase,
  IEmployee,
  IPersonalDetails,
  IPrivateEntrepreneurDetailsBase,
} from '../model/employee.types';
import { EMPLOYEES_TAG, EMPLOYEE_STATUS_TAG, EMPLOYEE_TAG, baseApi } from '@shared/api';
import { LOCAL_STORAGE_ACCESS_TOKEN_KEY } from '@shared/lib/contants';
import {
  EmployeeCreateDto,
  EmployeeListDto,
  ContactUpdateDto,
  IQueryParams,
  BankUpdateDto,
  EntrepreneurUpdateDto,
} from './employee.types';

const employeeApi = baseApi.injectEndpoints({
  endpoints: build => ({
    createEmployee: build.mutation<void, EmployeeCreateDto>({
      query: body => ({
        url: '/employees',
        method: 'POST',
        body,
      }),
      invalidatesTags: [EMPLOYEES_TAG],
    }),
    getEmployeeList: build.query<EmployeeListDto, IQueryParams>({
      query: params => ({
        url: '/employees',
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY)}`,
        },
      }),
      providesTags: [EMPLOYEES_TAG],
    }),
    getEmployee: build.query<IEmployee, string>({
      query: id => ({
        url: `/employees/${id}`,
      }),
      providesTags: [EMPLOYEE_STATUS_TAG, EMPLOYEE_TAG],
    }),
    updateEmployeeStatus: build.mutation<void, { id: string; isActive: boolean }>({
      query: ({ id, isActive }) => ({
        url: `/employees/${id}/status`,
        method: 'PATCH',
        body: {
          isActive,
        },
      }),
      invalidatesTags: [EMPLOYEES_TAG, EMPLOYEE_STATUS_TAG],
    }),
    updateEmployeePersonal: build.mutation<IPersonalDetails, { id: string; body: IPersonalDetails }>({
      query: ({ id, body }) => ({
        url: `employees/${id}/personal-info`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [EMPLOYEES_TAG, EMPLOYEE_TAG],
    }),
    updateEmployeeContact: build.mutation<IContactDetailsBase, { id: string; body: ContactUpdateDto }>({
      query: ({ id, body }) => ({
        url: `employees/${id}/contact`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [EMPLOYEES_TAG, EMPLOYEE_TAG],
    }),
    updateEmployeeBank: build.mutation<IBankDetailsBase, { id: string; body: BankUpdateDto }>({
      query: ({ id, body }) => ({
        url: `employees/${id}/bank-details`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [EMPLOYEES_TAG, EMPLOYEE_TAG],
    }),
    updateEmployeeEntrepreneur: build.mutation<
      IPrivateEntrepreneurDetailsBase,
      { id: string; body: EntrepreneurUpdateDto }
    >({
      query: ({ id, body }) => ({
        url: `employees/${id}/entrepreneur-info`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [EMPLOYEES_TAG, EMPLOYEE_TAG],
    }),
    getCsvTemplate: build.query<string, void>({
      query: () => ({
        url: 'employees/csv-template',
      }),
    }),
    uploadEmployeesCsv: build.mutation<string, FormData>({
      query: body => ({
        url: 'employees/csv-import',
        method: 'POST',
        body,
      }),
      invalidatesTags: [EMPLOYEES_TAG],
    }),
  }),
});

export const {
  useCreateEmployeeMutation,
  useGetEmployeeListQuery,
  useGetEmployeeQuery,
  useUpdateEmployeeStatusMutation,
  useUpdateEmployeePersonalMutation,
  useUpdateEmployeeContactMutation,
  useUpdateEmployeeBankMutation,
  useUpdateEmployeeEntrepreneurMutation,
  useLazyGetCsvTemplateQuery,
  useUploadEmployeesCsvMutation,
} = employeeApi;
